import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Box, Button, createStyles, Group, Input, SimpleGrid, Stack, Switch} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useState} from 'react';

import {
  DomainSequenceType,
  SequenceAnnotationConfig,
} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import GermlineSelector from 'antibodies/germline/GermlineSelector';
import {CodonTranslationTable} from 'biojs/BioConstants';
import FolderSelector from 'file-browser/FolderSelector';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {RunModalProps} from 'ui-library/runModal';

export const ANNOTATION_TOGGLE_DESCRIPTION =
  'Existing annotations on domain sequences will be removed and overwritten';

const COMPONENT_NAME = 'AnnotateDomainSequencesSettingsModal';

const useStyles = createStyles((theme) => ({
  fieldControl: {
    width: '100%',
    '& .selectDropdown, & .dropdownComponent, & .selectDropdown-button, & .folderSelector': {
      width: '100%',
    },
  },
  switchBody: {
    justifyContent: 'space-between',
  },
  switchLabelWrapper: {
    flex: 1,
  },
  switchLabel: {
    paddingLeft: 0,
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
  },
  switchDescription: {
    paddingLeft: 0,
  },
}));

function getAnnotationToggleLabel(sequenceType: DomainSequenceType): string {
  return `Annotate ${sequenceType === DomainSequenceType.DNA ? 'DNA' : 'protein'} domain sequences`;
}

/**
 * Closes with the annotation config, or with null when the user submits with annotation turned off.
 * Closing with X or Escape resolves undefined, which means the user cancelled the submit.
 */
export interface AnnotateDomainSequencesSettingsModalProps extends RunModalProps<SequenceAnnotationConfig | null> {
  sequenceType: DomainSequenceType;
  /** Prefills the folder for the annotation results receipt, typically with the entry's folder. */
  initialFolderId: string | null;
  submitButtonText?: string;
  /** Shown in an info banner at the top of the modal. No banner is shown when omitted. */
  infoBannerText?: string;
  /**
   * When false, annotation cannot be turned off, so the modal only closes with a config or undefined. The
   * overwrite warning is shown in a banner instead of on the toggle.
   */
  showAnnotationToggle?: boolean;
}

export default function AnnotateDomainSequencesSettingsModal({
  sequenceType,
  initialFolderId,
  submitButtonText = 'Submit',
  infoBannerText,
  showAnnotationToggle = true,
  close,
}: AnnotateDomainSequencesSettingsModalProps): JSX.Element {
  const {classes} = useStyles();
  const isDna = sequenceType === DomainSequenceType.DNA;
  const folderSelectorId = useId();
  const [isAnnotationEnabled, setIsAnnotationEnabled] = useState(true);
  const [germlineId, setGermlineId] = useState<string | null>(null);
  const [geneticCode, setGeneticCode] = useState<CodonTranslationTable | null>(
    isDna ? CodonTranslationTable.STANDARD : null
  );
  const [folderId, setFolderId] = useState<string | null>(initialFolderId);

  const isMissingRequiredFields = germlineId == null || folderId == null || (isDna && geneticCode == null);
  const isSubmitDisabled = isAnnotationEnabled && isMissingRequiredFields;

  function onSubmit(): void {
    if (!isAnnotationEnabled) {
      close(null);
      return;
    }
    if (germlineId == null || folderId == null) {
      return;
    }
    close({
      sequenceType,
      germlineId,
      geneticCode: isDna ? geneticCode : null,
      folderId,
    });
  }

  return (
    <div {...getTestProps({component: COMPONENT_NAME, element: 'root'})}>
      <Stack spacing="lg">
        {infoBannerText != null && (
          <Alert
            variant="info"
            icon={<BenchlingIcon icon="info-circle" />}
            title={infoBannerText}
            {...getTestProps({component: COMPONENT_NAME, element: 'infoBanner'})}
          >
            {null}
          </Alert>
        )}
        {showAnnotationToggle ? (
          <Switch
            label={getAnnotationToggleLabel(sequenceType)}
            description={ANNOTATION_TOGGLE_DESCRIPTION}
            labelPosition="left"
            checked={isAnnotationEnabled}
            onChange={(event) => {
              setIsAnnotationEnabled(event.currentTarget.checked);
            }}
            classNames={{
              body: classes.switchBody,
              labelWrapper: classes.switchLabelWrapper,
              label: classes.switchLabel,
              description: classes.switchDescription,
            }}
            {...getTestProps({component: COMPONENT_NAME, element: 'annotationToggle'})}
          />
        ) : (
          <Alert
            variant="warning"
            icon={<BenchlingIcon icon="warning" />}
            title={ANNOTATION_TOGGLE_DESCRIPTION}
            {...getTestProps({component: COMPONENT_NAME, element: 'overwriteWarningBanner'})}
          >
            {null}
          </Alert>
        )}
        {isAnnotationEnabled && (
          <SimpleGrid cols={2} spacing="md" verticalSpacing="md">
            <Input.Wrapper
              label="Germline"
              description="Select reference germline for domain annotation"
              required={true}
              {...getTestProps({component: COMPONENT_NAME, element: 'germline'})}
            >
              <Box className={classes.fieldControl}>
                <GermlineSelector
                  selectedGermlineId={germlineId}
                  onSelectGermline={setGermlineId}
                  disabled={false}
                />
              </Box>
            </Input.Wrapper>
            {isDna && (
              <Input.Wrapper
                label="Genetic code"
                description="Select genetic code for DNA to AA translation"
                required={true}
                {...getTestProps({component: COMPONENT_NAME, element: 'geneticCode'})}
              >
                <Box className={classes.fieldControl}>
                  <GeneticCodeDropdown geneticCode={geneticCode} onSelectGeneticCode={setGeneticCode} />
                </Box>
              </Input.Wrapper>
            )}
            <Input.Wrapper
              id={folderSelectorId}
              label="Folder"
              description="Send receipt of annotation results to selected folder"
              required={true}
              {...getTestProps({component: COMPONENT_NAME, element: 'folder'})}
            >
              <Box className={classes.fieldControl}>
                <FolderSelector
                  id={folderSelectorId}
                  value={folderId}
                  onChange={(e) => {
                    setFolderId(e.target.value ?? null);
                  }}
                  showInventory={true}
                  noFolderText="Choose folder"
                  enableRedesign={true}
                  selectorModalZIndex={ZINDEX_FLOATING_UI}
                />
              </Box>
            </Input.Wrapper>
          </SimpleGrid>
        )}
      </Stack>
      <ModalFooter>
        <Group position="right">
          <Button
            disabled={isSubmitDisabled}
            onClick={onSubmit}
            {...getTestProps({component: COMPONENT_NAME, element: 'submitButton'})}
          >
            {submitButtonText}
          </Button>
        </Group>
      </ModalFooter>
    </div>
  );
}
